import { useEffect, useState } from 'react';
import { LogOut, ShieldAlert } from 'lucide-react';
import { clearImpersonation, getStoredImpersonation } from '@/lib/impersonation';
import { portalApi } from '@/lib/partners';

/**
 * Shown on every page while a staff member is using Login as Partner in this tab: whose login,
 * which partner, how long is left, and a way out. When the time runs out the tab goes back to the
 * staff session by itself.
 */
export function ImpersonationBanner() {
  const [state] = useState(() => getStoredImpersonation());
  const [now, setNow] = useState(() => Date.now());
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    if (!state) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [state]);

  const leftMs = state ? new Date(state.expiresAt).getTime() - now : 0;

  useEffect(() => {
    if (state && leftMs <= 0) {
      clearImpersonation();
      window.location.assign(state.returnTo || '/partners');
    }
  }, [state, leftMs]);

  if (!state) return null;

  const end = async () => {
    setEnding(true);
    try {
      await portalApi.endImpersonation();
    } catch {
      // Already ended or expired on the server — leaving is still the right outcome.
    }
    clearImpersonation();
    window.location.assign(state.returnTo || '/partners');
  };

  const mins = Math.max(0, Math.floor(leftMs / 60000));
  const secs = Math.max(0, Math.floor((leftMs % 60000) / 1000));

  return (
    <div role="status" className="sticky top-0 z-40 flex flex-wrap items-center gap-x-3 gap-y-1 bg-violet-700 px-4 py-2 text-sm text-white">
      <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        You’re using the portal as <b>{state.partnerUser.name}</b> ({state.partnerUser.role}) at <b>{state.partner.name}</b>.
        {' '}Everything you do is recorded as you, acting on their behalf.
      </span>
      <span className="font-medium tabular-nums" aria-label={`Session ends in ${mins} minutes ${secs} seconds`}>
        Ends in {mins}:{String(secs).padStart(2, '0')}
      </span>
      <button
        type="button"
        onClick={end}
        disabled={ending}
        className="inline-flex items-center gap-1.5 rounded-md bg-white/15 px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-60"
      >
        <LogOut className="h-3.5 w-3.5" />{ending ? 'Ending…' : 'End session'}
      </button>
    </div>
  );
}
